import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import JinglePlayer from "@/components/JinglePlayer";
import { jingles } from "@/data/jingles";

// Assets
import logo from "@/assets/EMA-KO-Logo.png";
import heroGif from "@/assets/hero-headline.gif";
import ecomProfitBox from "@/assets/ecom-profit-box.png";
import beefTallow from "@/assets/beef-tallow.png";
import candleLifestyle from "@/assets/candle-lifestyle.png";
import iconFast from "@/assets/icon-fast-turnaround.png";
import iconAffordable from "@/assets/icon-affordable.png";
import iconBrand from "@/assets/icon-brand-aligned.png";
import uspMobile from "@/assets/usp-mobile.png";

// Price cards
import priceBronzeVideo from "@/assets/price-bronze-video.png";
import priceSilverVideo from "@/assets/price-silver-video.png";
import priceGoldVideo from "@/assets/price-gold-video.png";
import priceBronzePhoto from "@/assets/price-bronze-photo.png";
import priceSilverPhoto from "@/assets/price-silver-photo.png";
import priceGoldPhoto from "@/assets/price-gold-photo.png";
import priceBronzeGraphic from "@/assets/price-bronze-graphic.png";
import priceSilverGraphic from "@/assets/price-silver-graphic.png";
import priceGoldGraphic from "@/assets/price-gold-graphic.png";
import priceBronzeCombo from "@/assets/price-bronze-combo.png";
import priceSilverCombo from "@/assets/price-silver-combo.png";
import priceGoldCombo from "@/assets/price-gold-combo.png";
import priceBronzeBundle from "@/assets/price-bronze-bundle.png";
import priceSilverBundle from "@/assets/price-silver-bundle.png";
import priceGoldBundle from "@/assets/price-gold-bundle.png";
import priceAiPhotoshoot from "@/assets/price-ai-photoshoot.png";
import priceAmazonStore from "@/assets/price-amazon-brand-store.png";
import priceAmazonStory from "@/assets/price-amazon-brand-story.png";
import priceAmazonKw from "@/assets/price-amazon-kw-research.png";
import priceUgc from "@/assets/price-ugc-review.png";
import pricePartTime from "@/assets/price-part-time-marketing.png";

const scrollTo = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

const LazyVimeoEmbed = ({ id, title }: { id: string; title: string }) => {
  const [thumb, setThumb] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${id}&width=1280`)
      .then((r) => r.json())
      .then((data) => data.thumbnail_url && setThumb(data.thumbnail_url))
      .catch(() => {});
  }, [id]);

  if (loaded) {
    return (
      <iframe
        src={`https://player.vimeo.com/video/${id}?badge=0&autopause=0&player_id=0&app_id=58479&dnt=1&autoplay=1`}
        className="w-full h-full"
        allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
        allowFullScreen
        title={title}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setLoaded(true)}
      className="group relative flex h-full w-full items-center justify-center overflow-hidden bg-section-dark text-section-dark-foreground"
      aria-label={`Play ${title}`}
    >
      {thumb && (
        <img
          src={thumb}
          alt={title}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          loading="lazy"
        />
      )}
      <span className="relative z-10 flex h-16 w-16 items-center justify-center rounded-full bg-background/90 transition group-hover:scale-105">
        <span className="ml-1 h-0 w-0 border-y-[10px] border-l-[16px] border-y-transparent border-l-primary" />
      </span>
    </button>
  );
};

/* ─── HERO ─── */
const Hero = () => (
  <section className="bg-black py-16 md:py-24">
    <div className="max-w-5xl mx-auto px-4 text-center">
      <h1 className="sr-only">Amazon Product Photography & Video Production Services | Ecommerce Content Creation Agency | Professional Amazon Listing Photos & Videos | Product Photoshoot & Videography for Amazon Sellers | A+ Content Design | Amazon Brand Store Design | Lifestyle Product Photography | White Background Product Photos | UGC Video Content for Amazon & Shopify</h1>
      <img src={heroGif} alt="vid. photo. design." className="mx-auto mb-6 max-w-4xl w-full" />
      <p className="text-section-dark-foreground/80 text-xl md:text-2xl max-w-2xl mx-auto mb-8">
        Content that drives sales. Easy to order. Built to grow.
      </p>
      <div className="aspect-video max-w-3xl mx-auto mb-8 rounded-lg overflow-hidden">
        <LazyVimeoEmbed id="925045291" title="Evolve Media Agency Reel" />
      </div>
      <p className="text-white text-sm md:text-base text-center max-w-3xl mx-auto py-4 mb-4">
        We're a content factory of video, photo, and listing experts for Amazon and ecommerce brands that want more clicks, more conversions, and more profit.
      </p>
      <a
        href="https://calendly.com/evolvemedia/amz-30-min"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block bg-cta-blue hover:bg-cta-blue-hover text-white px-8 py-3 rounded-full text-[1.125rem] transition"
      >
        Get Free Brand Audit
      </a>
    </div>
  </section>
);

/* ─── OPT-IN SECTION ─── */
const OptIn = () => (
  <section className="bg-section-light py-16 md:py-24">
    <div className="max-w-6xl mx-auto px-8 lg:px-16 flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
      <a href="https://emapdf.com/free" target="_blank" rel="noopener noreferrer">
        <img src={ecomProfitBox} alt="11 Free Proven Guides" className="w-full max-w-md md:max-w-lg" />
      </a>
      <div className="flex-1">
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 leading-tight">
          11 Free Guides to launch faster + convert better
        </h2>
        <p className="text-foreground/70 mb-6 text-base md:text-lg leading-relaxed">
          The same frameworks we use with real brands. The stuff most sellers never slow down to think about.
        </p>
        <ul className="list-disc list-inside space-y-2 text-foreground/80 mb-8 text-base md:text-lg">
          <li>How to structure listings that convert</li>
          <li>What makes main images get clicked</li>
          <li>Expert launch checklists</li>
          <li>Offer + bundle ideas for higher AOV</li>
          <li>Proven email conversion tips</li>
        </ul>
        <a href="https://emapdf.com/free" target="_blank" rel="noopener noreferrer" className="inline-block bg-cta-green hover:bg-cta-green-hover text-white px-6 py-3 rounded-full transition animate-wobble text-[1.125rem]">
          Get Free PDFs
        </a>
      </div>
    </div>
  </section>
);

/* ─── PORTFOLIOS ─── */
const Portfolios = () => {
  const [showMorePhotos, setShowMorePhotos] = useState(false);
  const [showMoreVideos, setShowMoreVideos] = useState(false);

  useEffect(() => {
    const scriptSrc = "https://cdn.commoninja.com/sdk/latest/commonninja.js";
    const existingScript = document.querySelector(`script[src="${scriptSrc}"]`);
    if (!existingScript) {
      const script = document.createElement("script");
      script.src = scriptSrc;
      script.defer = true;
      document.body.appendChild(script);
    }
  }, []);

  return (
    <section className="bg-background py-16 md:py-20">
      <div className="max-w-6xl mx-auto px-4 my-0">
        <h2 className="text-4xl md:text-5xl font-bold text-center text-foreground mb-4">
          Content built for conversions.
        </h2>
        <p className="text-center max-w-2xl mx-auto mb-8 text-base md:text-lg text-primary">
          Photos. Videos. A+ Content. UGC. Full listing systems.
        </p>

        {/* Photo Portfolio */}
        <p className="text-center mb-4">
          <a href="/product-photos" className="text-cta-blue hover:underline font-semibold text-base md:text-lg">
            View full photo portfolio here.
          </a>
        </p>
        <div className="mb-8">
          <div className="commonninja_component pid-b50c9c2e-e36b-4746-80c7-c6db421a97b5"></div>
        </div>
        <div className={showMorePhotos ? "mb-8 space-y-8" : "mb-8 space-y-8 hidden"}>
          <div className="commonninja_component pid-c97ab087-8762-471a-948e-48bc09bd48bb"></div>
          <div className="commonninja_component pid-22806446-a693-418b-b532-58d33d66987d"></div>
        </div>
        <div className="text-center mb-20 md:mb-24">
          <button
            onClick={() => setShowMorePhotos(!showMorePhotos)}
            className="bg-cta-green hover:bg-cta-green-hover text-white px-6 py-2.5 rounded-full transition text-[1.125rem]"
          >
            {showMorePhotos ? "Show Less" : "Load More"}
          </button>
        </div>

        {/* Video Portfolio */}
        <p className="text-center mb-4">
          <a href="/video-portfolio" className="text-cta-blue hover:underline font-semibold text-base md:text-lg">
            View full video portfolio here.
          </a>
        </p>
        <div className="mb-10">
          <div className="commonninja_component pid-83754712-159a-427b-a408-cbf7f1a1552c"></div>
        </div>
        <div className={showMoreVideos ? "mb-10 space-y-6" : "mb-10 hidden"}>
          <div className="commonninja_component pid-319e686a-5af2-4e24-aa12-2f9ea5af11e2"></div>
          <div className="commonninja_component pid-ce6d61ea-6bd8-4dbd-9caa-fa63666b9994"></div>
        </div>
        <div className="text-center">
          <button
            onClick={() => setShowMoreVideos(!showMoreVideos)}
            className="bg-cta-green hover:bg-cta-green-hover text-white px-6 py-2.5 rounded-full transition text-[1.125rem]"
          >
            {showMoreVideos ? "Show Less" : "Load More"}
          </button>
        </div>
      </div>
    </section>
  );
};

/* ─── JINGLES SHOWCASE ─── */
const JinglesShowcase = () => {
  const featured = jingles.slice(0, 6);

  return (
    <section className="bg-section-light py-16 md:py-20">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <p className="text-cta-blue font-extrabold text-xs tracking-[2px] uppercase mb-3">
            SONIC BRANDING · 63 ORIGINAL TRACKS
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            Custom jingles built to get <span className="text-cta-green">stuck</span> in a customer's head.
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Original jingles, audio logos, and sonic branding for Amazon sellers and ecommerce brands.
            Hit play on any track below — every one was written, recorded, and mixed from scratch for a real brand.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
          {featured.map((j) => (
            <article
              key={j.audioSlug}
              className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-shadow p-5 flex flex-col"
            >
              <div className="mb-2">
                <span className="inline-block text-[11px] uppercase tracking-wider font-bold text-cta-blue bg-cta-blue/10 px-2 py-1 rounded">
                  {j.industry}
                </span>
              </div>
              <h3 className="font-black text-foreground text-lg leading-tight">{j.brand}</h3>
              <p className="text-sm text-muted-foreground mb-3">{j.product}</p>
              <div className="flex flex-wrap gap-1.5 mb-1 text-[11px]">
                <span className="bg-muted px-2 py-0.5 rounded text-foreground/70">{j.genreLabel}</span>
                <span className="bg-muted px-2 py-0.5 rounded text-foreground/70 capitalize">{j.mood}</span>
                <span className="bg-muted px-2 py-0.5 rounded text-foreground/70 capitalize">{j.vocal} vocal</span>
              </div>
              <JinglePlayer
                src={`https://audio.evolveamz.com/${j.audioSlug}.mp3`}
                title={`${j.brand} ${j.product}`}
              />
            </article>
          ))}
        </div>

        <div className="text-center">
          <Link
            to="/custom-jingles-sonic-branding"
            className="inline-block bg-cta-blue hover:bg-cta-blue-hover text-white px-8 py-3 rounded-full transition text-[1.125rem] font-semibold"
          >
            🎵 Listen to All 63 Custom Jingles
          </Link>
        </div>
      </div>
    </section>
  );
};

/* ─── USP ROW ─── */
const USPRow = () => {
  const items = [
    { icon: iconFast, title: "Fast Turnaround" },
    { icon: iconAffordable, title: "Affordable Packages" },
    { icon: iconBrand, title: "Brand-Aligned" },
  ];
  return (
    <section className="bg-section-light py-12">
      {/* Mobile: single stacked image */}
      <div className="md:hidden max-w-sm mx-auto px-4">
        <img src={uspMobile} alt="Fast Turnaround, Affordable Packages, Brand-Aligned" className="w-full" />
      </div>
      {/* Desktop: icon grid */}
      <div className="hidden md:grid max-w-5xl mx-auto px-4 grid-cols-3 gap-4 text-center">
        {items.map((item) => (
          <div key={item.title} className="flex flex-col items-center">
            <img src={item.icon} alt={item.title} className="w-72 h-auto object-contain" />
          </div>
        ))}
      </div>
    </section>
  );
};

/* ─── LIFESTYLE / BRAND STORE READY ─── */
const BrandStoreReady = () => (
  <section className="bg-background py-16 md:py-20">
    <div className="max-w-6xl mx-auto px-4 md:px-6">
      <h2 className="text-4xl md:text-5xl font-bold text-center text-foreground mb-3">
        From basic white backgrounds<br />to brand store ready
      </h2>
      <p className="text-center mb-16 md:mb-20 text-base md:text-lg text-primary">
        We capture store-ready visuals that get more than just clicks.
      </p>

      <div className="mx-auto max-w-5xl space-y-24 md:space-y-32">
        <div className="grid items-center justify-center gap-10 lg:gap-14 lg:grid-cols-[minmax(0,1fr)_minmax(320px,420px)]">
          <div className="mx-auto w-full max-w-md text-center lg:text-right lg:justify-self-end">
            <ul className="space-y-3 md:space-y-4">
              <li className="text-xl md:text-[1.75rem] leading-tight font-bold text-foreground">White Background</li>
              <li className="text-xl md:text-[1.75rem] leading-tight font-bold text-foreground">Color Studio</li>
              <li className="text-xl md:text-[1.75rem] leading-tight font-bold text-foreground">Full-body Models</li>
              <li className="text-xl md:text-[1.75rem] leading-tight font-bold text-foreground">Lifestyle Scenes</li>
              <li className="text-xl md:text-[1.75rem] leading-tight font-bold text-foreground">Destination Shoots</li>
            </ul>
          </div>
          <img src={beefTallow} alt="Tallow balm lifestyle" className="w-full max-w-sm lg:max-w-[420px] rounded-lg shadow-lg mx-auto lg:mx-0" />
        </div>

        <div className="grid items-center justify-center gap-10 lg:gap-14 lg:grid-cols-[minmax(320px,420px)_minmax(0,1fr)]">
          <img src={candleLifestyle} alt="Candle lifestyle" className="w-full max-w-sm lg:max-w-[420px] rounded-lg shadow-lg mx-auto lg:mx-0" />
          <div className="mx-auto w-full max-w-md text-center lg:text-left lg:justify-self-start">
            <ul className="space-y-3 md:space-y-4">
              <li className="text-xl md:text-[1.75rem] leading-tight font-bold text-foreground">Order Same Day</li>
              <li className="text-xl md:text-[1.75rem] leading-tight font-bold text-foreground">Shoot This Week</li>
              <li className="text-xl md:text-[1.75rem] leading-tight font-bold text-foreground">Retail-Ready Assets</li>
              <li className="text-xl md:text-[1.75rem] leading-tight font-bold text-foreground">Full Brand Store Buildouts</li>
              <li className="text-xl md:text-[1.75rem] leading-tight font-bold text-foreground">Support Beyond Launch</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ─── RESOURCES & STATS ─── */
const ResourcesAndStats = () => (
  <section className="bg-section-light py-16 md:py-20">
    <div className="max-w-6xl mx-auto px-4 md:px-6">
      <h2 className="text-4xl md:text-5xl font-bold text-center text-foreground mb-4">
        Why visual content matters for Amazon sellers
      </h2>
      <p className="text-center text-muted-foreground mb-12 max-w-3xl mx-auto text-base md:text-lg">
        Industry research shows that high-quality product visuals directly impact conversion rates, click-through rates, and customer trust on Amazon and ecommerce platforms.
      </p>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
        {/* Stat 1 */}
        <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-cta-blue">
          <p className="text-4xl font-bold text-cta-blue mb-2">63%</p>
          <p className="text-foreground font-semibold mb-2">of consumers say product images are more important than product descriptions</p>
          <p className="text-sm text-muted-foreground">— Multiple ecommerce conversion studies</p>
        </div>

        {/* Stat 2 */}
        <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-cta-green">
          <p className="text-4xl font-bold text-cta-green mb-2">94%</p>
          <p className="text-foreground font-semibold mb-2">higher conversion rates for listings with lifestyle product photography vs. white background only</p>
          <p className="text-sm text-muted-foreground">— Amazon seller research data</p>
        </div>

        {/* Stat 3 */}
        <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-cta-blue">
          <p className="text-4xl font-bold text-cta-blue mb-2">73%</p>
          <p className="text-foreground font-semibold mb-2">of Amazon shoppers watch product videos before making a purchase decision</p>
          <p className="text-sm text-muted-foreground">— Amazon marketplace insights</p>
        </div>
      </div>

      {/* Resources Grid */}
      <div className="bg-white p-8 rounded-lg shadow-lg">
        <h3 className="text-2xl md:text-3xl font-bold text-center text-foreground mb-8">
          Explore our work & resources
        </h3>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <Link
            to="/video-portfolio"
            className="p-6 bg-gradient-to-br from-cta-blue/10 to-cta-blue/5 rounded-lg hover:shadow-lg transition-all border-2 border-cta-blue/20 hover:border-cta-blue/40"
          >
            <h4 className="text-xl font-bold text-foreground mb-2">📹 Video Portfolio</h4>
            <p className="text-muted-foreground text-sm">See our Amazon product videos, lifestyle shoots, and UGC content</p>
          </Link>

          <Link
            to="/product-photos"
            className="p-6 bg-gradient-to-br from-cta-green/10 to-cta-green/5 rounded-lg hover:shadow-lg transition-all border-2 border-cta-green/20 hover:border-cta-green/40"
          >
            <h4 className="text-xl font-bold text-foreground mb-2">📸 Photo Portfolio</h4>
            <p className="text-muted-foreground text-sm">Browse our product photography, lifestyle images, and Amazon main images</p>
          </Link>

          <Link
            to="/ai"
            className="p-6 bg-gradient-to-br from-cta-blue/10 to-cta-blue/5 rounded-lg hover:shadow-lg transition-all border-2 border-cta-blue/20 hover:border-cta-blue/40"
          >
            <h4 className="text-xl font-bold text-foreground mb-2">🤖 AI Photoshoot</h4>
            <p className="text-muted-foreground text-sm">Discover our AI-powered product photography service</p>
          </Link>

          <Link
            to="/blog"
            className="p-6 bg-gradient-to-br from-cta-green/10 to-cta-green/5 rounded-lg hover:shadow-lg transition-all border-2 border-cta-green/20 hover:border-cta-green/40"
          >
            <h4 className="text-xl font-bold text-foreground mb-2">📝 Blog & Insights</h4>
            <p className="text-muted-foreground text-sm">Amazon selling tips, content strategies, and conversion optimization</p>
          </Link>

          <Link
            to="/blog/amazon-product-launch-strategy-2026"
            className="p-6 bg-gradient-to-br from-amber-500/10 to-amber-500/5 rounded-lg hover:shadow-lg transition-all border-2 border-amber-500/20 hover:border-amber-500/40"
          >
            <h4 className="text-xl font-bold text-foreground mb-2">🚀 Product Launch Playbook</h4>
            <p className="text-muted-foreground text-sm">Complete step-by-step guide to launching on Amazon in 2026</p>
          </Link>

          <a
            href="https://evolveamz.com/ecom-growth-guides/"
            target="_blank"
            rel="noopener noreferrer"
            className="p-6 bg-gradient-to-br from-cta-blue/10 to-cta-blue/5 rounded-lg hover:shadow-lg transition-all border-2 border-cta-blue/20 hover:border-cta-blue/40"
          >
            <h4 className="text-xl font-bold text-foreground mb-2">📚 Free Growth Guides</h4>
            <p className="text-muted-foreground text-sm">11 proven frameworks for Amazon listing optimization and growth</p>
          </a>

          <Link
            to="/amazon-product-video-production"
            className="p-6 bg-gradient-to-br from-cta-green/10 to-cta-green/5 rounded-lg hover:shadow-lg transition-all border-2 border-cta-green/20 hover:border-cta-green/40"
          >
            <h4 className="text-xl font-bold text-foreground mb-2">🎬 Video Production Guide</h4>
            <p className="text-muted-foreground text-sm">Why Amazon product videos sell more than photos alone</p>
          </Link>

          <Link
            to="/custom-jingles-sonic-branding"
            className="p-6 bg-gradient-to-br from-amber-500/10 to-amber-500/5 rounded-lg hover:shadow-lg transition-all border-2 border-amber-500/20 hover:border-amber-500/40"
          >
            <h4 className="text-xl font-bold text-foreground mb-2">🎵 Listen to 63 Custom Jingles</h4>
            <p className="text-muted-foreground text-sm">Original sonic branding portfolio — hit play on any track</p>
          </Link>

          <Link
            to="/shopify-web-development"
            className="p-6 bg-gradient-to-br from-cta-green/10 to-cta-green/5 rounded-lg hover:shadow-lg transition-all border-2 border-cta-green/20 hover:border-cta-green/40"
          >
            <h4 className="text-xl font-bold text-foreground mb-2">💻 Shopify & Web Development</h4>
            <p className="text-muted-foreground text-sm">Stores, landing pages, and funnels engineered for CTR + CVR</p>
          </Link>

          <Link
            to="/amazon-brand-store-design"
            className="p-6 bg-gradient-to-br from-cta-blue/10 to-cta-blue/5 rounded-lg hover:shadow-lg transition-all border-2 border-cta-blue/20 hover:border-cta-blue/40"
          >
            <h4 className="text-xl font-bold text-foreground mb-2">🏪 Brand Store Design</h4>
            <p className="text-muted-foreground text-sm">Complete guide to building a storefront that converts</p>
          </Link>

          <Link
            to="/ecommerce-content-roi"
            className="p-6 bg-gradient-to-br from-cta-green/10 to-cta-green/5 rounded-lg hover:shadow-lg transition-all border-2 border-cta-green/20 hover:border-cta-green/40"
          >
            <h4 className="text-xl font-bold text-foreground mb-2">💰 Content ROI Calculator</h4>
            <p className="text-muted-foreground text-sm">Why professional content pays for itself in saved ad spend</p>
          </Link>

          <a
            href="https://emapdf.com/free"
            target="_blank"
            rel="noopener noreferrer"
            className="p-6 bg-gradient-to-br from-cta-blue/10 to-cta-blue/5 rounded-lg hover:shadow-lg transition-all border-2 border-cta-blue/20 hover:border-cta-blue/40"
          >
            <h4 className="text-xl font-bold text-foreground mb-2">📥 11 Free Ecom PDFs</h4>
            <p className="text-muted-foreground text-sm">Launch checklists, listing frameworks, and offer ideas — all free at emapdf.com</p>
          </a>
        </div>

        <p className="text-center text-sm text-muted-foreground italic">
          "Know your customer better than they know themselves. That's when content converts." — Expert sellers focus on customer research before creating content.
        </p>
      </div>
    </div>
  </section>
);

/* ─── PRICING ─── */
const videoRow = [priceBronzeVideo, priceSilverVideo, priceGoldVideo];
const photoRow = [priceBronzePhoto, priceSilverPhoto, priceGoldPhoto];
const bundleRow = [priceBronzeBundle, priceSilverBundle, priceGoldBundle];

const hiddenRows = [
  [priceBronzeCombo, priceSilverCombo, priceGoldCombo],
  [priceBronzeGraphic, priceSilverGraphic, priceGoldGraphic],
  [priceAmazonStory, priceAmazonStore, priceAmazonKw],
  [priceUgc, priceAiPhotoshoot, pricePartTime],
];

const PriceRow = ({ cards, className = "" }: { cards: string[]; className?: string }) => (
  <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 ${className}`}>
    {cards.map((src, i) => (
      <img
        key={i}
        src={src}
        alt={`Pricing package ${i + 1}`}
        className="w-full rounded-lg shadow-lg hover:scale-[1.02] transition-transform duration-200"
      />
    ))}
  </div>
);

const Pricing = () => {
  const [showMore, setShowMore] = useState(false);

  return (
    <section id="pricing" className="bg-black py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4">
        <h2 className="text-4xl md:text-5xl font-bold text-center text-section-dark-foreground mb-3">
          Simple pricing. Done fast.
        </h2>
        <p className="text-center text-section-dark-foreground/60 mb-12 max-w-xl mx-auto text-base md:text-lg text-white">
          Get only what you need. No retainers required.<br />
          Just choose your level. Add bundles. Scale when ready.
        </p>

        {/* Video row */}
        <PriceRow cards={videoRow} />

        {/* Photo row */}
        <div className="mt-6">
          <PriceRow cards={photoRow} />
        </div>

        {/* Content Bundle row – blue background */}
        <div className="mt-6 rounded-xl p-6 bg-cta-blue">
          <PriceRow cards={bundleRow} />
        </div>

        {/* Hidden rows */}
        {showMore && (
          <div className="mt-6 space-y-6">
            {hiddenRows.map((row, i) => (
              <PriceRow key={i} cards={row} />
            ))}
          </div>
        )}

        <div className="text-center mt-10">
          <button
            onClick={() => setShowMore(!showMore)}
            className="bg-cta-green hover:bg-cta-green-hover text-white px-8 py-3 rounded-full transition text-[1.125rem]"
          >
            {showMore ? "Show Less" : "Load More"}
          </button>
        </div>
      </div>
    </section>
  );
};

/* ─── REVIEWS ─── */
const Reviews = () => (
  <section className="bg-background py-16 md:py-20">
    <div className="max-w-5xl mx-auto px-4 text-center">
      <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-10">What brands say<br />after working with us</h2>
      <div className="aspect-video max-w-3xl mx-auto mb-10 rounded-lg overflow-hidden">
        <LazyVimeoEmbed id="993024341" title="Client Review" />
      </div>
      <div className="commonninja_component pid-1e622c7b-461c-4f31-b6cf-464ad7efb4da"></div>
    </div>
  </section>
);

/* ─── CONTACT ─── */
const Contact = () => (
  <section id="contact" className="bg-section-light py-16 md:py-24">
    <div className="max-w-4xl mx-auto px-4 text-center">
      <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-3">Let's look at your listing</h2>
      <p className="text-muted-foreground mb-4 max-w-2xl mx-auto text-base md:text-lg">
        Book a free brand audit and we'll review your main image, content gaps, and missed opportunities live. You'll walk away with real feedback — whether we work together or not.
      </p>
      <p className="text-foreground font-semibold mb-10 text-base md:text-lg">
        Reach out at{" "}
        <a href="tel:7192573557" className="hover:text-cta-blue transition underline">719-257-3557</a>
        {" "}or{" "}
        <a href="mailto:ian@EvolveMediaAgency.com" className="hover:text-cta-blue transition underline">ian@EvolveMediaAgency.com</a>
      </p>
      <a
        href="https://calendly.com/evolvemedia/amz-30-min"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block bg-cta-green hover:bg-cta-green-hover text-white px-6 py-3 rounded-full transition animate-wobble text-[1.125rem]"
      >
        Book Free Brand Strategy Audit
      </a>
    </div>
  </section>
);

/* ─── PAGE ─── */
const Index = () => (
  <div className="min-h-screen">
    <Navbar />
    <Hero />
    <OptIn />
    <Portfolios />
    <JinglesShowcase />
    <USPRow />
    <BrandStoreReady />
    <ResourcesAndStats />
    <Pricing />
    <Reviews />
    <Contact />
    <Footer />
  </div>
);

export default Index;
